import { FC, useMemo, useState } from 'react';
import { observer } from 'mobx-react';

import { ImagePreviewToolbar } from '@/comp/Images/ImagePreviewToolbar/ImagePreviewToolbar';
import { ImagePreviewTagHeader } from '@/comp/Images/ImagePreviewTagHeader/ImagePreviewTagHeader';
import { ImagePreviewGroup } from '@/comp/Images/ImagePreviewGroup';
import { ImageData } from '@/store/SiteSurveyForm/constants';
import { useStores } from '@/hooks/use-stores';
import { ImageTags } from '@/constants/CompressorImageTagData.const';

type Props = {
  images: ImageData[];
  currentImageIndex: number;
  setCurrentImageIndex: (index: number) => void;
  onRemoveCompressorImage: (id: number | string, tag: ImageTags) => void;
  onEditTag: (id: number | string, newTag: ImageTags, currentTag: ImageTags) => void;
  visible: boolean;
  closePreview: () => void;
  mapView?: boolean;
};

export const ImageEditPreview: FC<Props> = observer(
  ({
    images,
    onRemoveCompressorImage,
    visible,
    closePreview,
    currentImageIndex,
    setCurrentImageIndex,
    onEditTag,
    mapView,
  }) => {
    const [showEdit, setShowEdit] = useState(false);
    const { imageUploadStore } = useStores();
    const selectedImage = useMemo(() => images[currentImageIndex], [currentImageIndex, images]);
    const currentIdentifier = mapView ? selectedImage?.encodedS3Key : selectedImage?.id;

    const editTag = (tag: ImageTags) => onEditTag(currentIdentifier, tag, selectedImage.tag);
    const removeCompressorImage = () => onRemoveCompressorImage(currentIdentifier, selectedImage.tag);

    return (
      <ImagePreviewGroup
        total={images.length}
        items={images.map(({ url, originalName }) => ({ src: url, alt: originalName }))}
        preview={{
          title: (
            <ImagePreviewTagHeader
              id={selectedImage?.id != null ? selectedImage.id.toString() : ''}
              filename={selectedImage?.originalName}
              tag={selectedImage?.tag}
              onEdit={editTag}
              currentlyEditing={showEdit}
              setCurrentlyEditing={setShowEdit}
              disabled={imageUploadStore.noEditPermission}
            />
          ),
          toolbarRender: (_, { image, ...props }) => (
            <ImagePreviewToolbar
              image={{ name: selectedImage?.originalName, ...image }}
              onDelete={removeCompressorImage}
              disabled={imageUploadStore.noEditPermission}
              {...props}
            />
          ),
          visible,
          onVisibleChange: closePreview,
          current: currentImageIndex,
          onChange: setCurrentImageIndex,
        }}
      />
    );
  },
);
